import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import BackBar from "../components/Ticketing/BackBar";
import { apiGetMyHolds, apiGetMyTickets } from "../api/ticketingApi";
import { fmtDateTime, fmtTime } from "../components/Ticketing/format";
import { loadTicketsIndex, saveTicketsIndex } from "../utils/ticketCache";

// Render `displayStatus`, not `status`. A RE_ENTRY ticket keeps status "VALID"
// after admission — that is what lets the holder back in — so rendering `status`
// showed a pristine green badge for a ticket already through the gate. The
// backend computes displayStatus so web, Expo and iOS cannot drift on it.
const statusColor = (s) =>
  s === "USED" ? "#6b7280"
  : s === "VOID" || s === "REFUNDED" ? "#dc2626"
  : s === "ADMITTED" ? "#b45309"
  : "#4d7a10";

// The raw enum was printed straight to customers ("USED", "VALID").
const statusLabel = (s) =>
  ({ USED: "Used", VALID: "Valid", ADMITTED: "Admitted", VOID: "Void", REFUNDED: "Refunded" }[s] || s);

/**
 * Live countdown to a reservation's expiry.
 *
 * "Finish paying before the hold expires" is not actionable without a number:
 * a customer whose payment just failed cannot tell whether they have four
 * minutes to retry or forty seconds. Removes its own row at zero, so "Pay"
 * never stays clickable on seats that are already back on sale — clicking it
 * would fail at checkout with a confusing error.
 */
const HoldCountdown = ({ expiresAt, onExpired }) => {
  const msLeft = () => new Date(expiresAt).getTime() - Date.now();
  const [remaining, setRemaining] = useState(msLeft);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => {
      const next = msLeft();
      setRemaining(next);
      if (next <= 0) {
        clearInterval(id);
        onExpired?.();
      }
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);

  if (!expiresAt || remaining <= 0) return null;
  const total = Math.floor(remaining / 1000);
  const mm = Math.floor(total / 60);
  const ss = String(total % 60).padStart(2, "0");
  const urgent = remaining <= 120000; // when they need to feel it

  return (
    <HoldTimer $urgent={urgent}>
      {urgent ? "⏱ " : ""}
      {mm}:{ss} left to pay
    </HoldTimer>
  );
};

const MyTicketsPage = () => {
  const history = useHistory();
  const [tickets, setTickets] = useState([]);
  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Non-null when the rows on screen came out of localStorage, not the network.
  const [cachedAt, setCachedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    apiGetMyTickets()
      .then((d) => {
        if (!alive) return;
        const rows = d.tickets || [];
        setTickets(rows);
        setCachedAt(null);
        setLoading(false);
        // Every row is serialised through the same `serializeTicket` the
        // single-ticket endpoint uses, so this list already carries a fresh
        // qrToken per VALID ticket. Writing it here makes every ticket
        // openable with no network, not just the ones already visited.
        saveTicketsIndex(rows);
      })
      .catch(() => {
        if (!alive) return;
        // No signal at doors-open is exactly when this list matters most, so
        // the dead end "We couldn't load your tickets." is the wrong answer
        // when a usable copy is sitting on disk.
        const cached = loadTicketsIndex();
        if (cached && cached.index.length) {
          setTickets(
            cached.index.map((row) => ({
              _id: row._id,
              serial: row.serial,
              ticketTypeName: row.ticketTypeName,
              seatLabel: row.seatLabel,
              status: row.status,
              displayStatus: row.displayStatus,
              event: row.eventTitle ? { title: row.eventTitle } : null,
              showing: row.startsAt ? { startsAt: row.startsAt } : null,
            }))
          );
          setCachedAt(cached.savedAt);
        } else {
          setError("We couldn't load your tickets.");
        }
        setLoading(false);
      });
    // Reservations load independently: a failure here must not blank the
    // tickets the customer HAS paid for.
    apiGetMyHolds()
      .then((d) => alive && setHolds(d.holds || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <Navbar />
      <Page>
        <BackBar fallback="/" />
        <h1>My Tickets</h1>
        {loading && <Muted>Loading…</Muted>}
        {!loading && error && <Muted>{error}</Muted>}
        {cachedAt != null && (
          <OfflineNote>
            Offline copy — last updated at {fmtTime(new Date(cachedAt).toISOString())}
          </OfflineNote>
        )}
        {/* Reservations first — these are time-critical and the tickets below
            are not. A web customer whose payment failed previously saw only
            "You have no tickets yet" while their seats were still held. */}
        {holds.length > 0 && (
          <HoldsBlock>
            <SectionTitle>Reservations</SectionTitle>
            <SectionSub>
              Seats held for you. Finish paying before the hold expires or they
              go back on sale.
            </SectionSub>
            <List>
              {holds.map((h) => {
                const seatLabels = (h.seats || [])
                  .map((x) => x.label || x.seatId)
                  .filter(Boolean)
                  .join(", ");
                const gaCount = (h.gaItems || []).reduce(
                  (a, i) => a + Number(i.qty || 0),
                  0
                );
                return (
                  <HoldRow key={String(h._id)}>
                    <div>
                      <TName>{h.event?.title || "Reserved seats"}</TName>
                      <TMeta>
                        {seatLabels
                          ? `Seats ${seatLabels}`
                          : gaCount
                            ? `${gaCount} ticket${gaCount === 1 ? "" : "s"}`
                            : ""}
                      </TMeta>
                      <HoldCountdown
                        expiresAt={h.expiresAt}
                        onExpired={() =>
                          setHolds((prev) =>
                            prev.filter((x) => String(x._id) !== String(h._id))
                          )
                        }
                      />
                    </div>
                    <PayBtn onClick={() => history.push(`/ticket-checkout/${h._id}`)}>
                      Pay
                    </PayBtn>
                  </HoldRow>
                );
              })}
            </List>
          </HoldsBlock>
        )}
        {!loading && !error && tickets.length === 0 && holds.length === 0 && (
          <Muted>You have no tickets yet.</Muted>
        )}
        {tickets.length > 0 && holds.length > 0 && (
          <SectionTitle>Your tickets</SectionTitle>
        )}
        <List>
          {tickets.map((t) => (
            <Row key={t._id} onClick={() => history.push(`/ticket/${t._id}`)}>
              <div>
                {/* The event title — without it a ticket read "Regular · Seat B12"
                    with no indication of WHAT it was for. */}
                <TName>{t.event?.title || t.ticketTypeName || "Ticket"}</TName>
                <TMeta>
                  {t.ticketTypeName || "Ticket"}{t.seatLabel ? ` · Seat ${t.seatLabel}` : ""}
                </TMeta>
                <TMeta>
                  {t.showing?.startsAt ? fmtDateTime(t.showing.startsAt) : ""} · {t.serial}
                </TMeta>
              </div>
              <Status style={{ color: statusColor(t.displayStatus || t.status) }}>
                {statusLabel(t.displayStatus || t.status)}
              </Status>
            </Row>
          ))}
        </List>
      </Page>
    </>
  );
};

const Page = styled.div`
  max-width: 720px; margin: 0 auto;
  padding: calc(var(--lusuku-nav-h, 130px) + 12px) 16px 48px; min-height: 60vh;
  h1 { font-size: 24px; font-weight: 800; color:#1f2937; margin:0 0 18px; }
`;
const Muted = styled.div`color:#6b7280;padding:20px 0;`;
const OfflineNote = styled.div`
  margin-bottom:14px;padding:8px 12px;border:1px solid #fde68a;border-radius:10px;
  background:#fffbeb;color:#b45309;font-size:12.5px;font-weight:600;
`;
const HoldsBlock = styled.div`margin-bottom:18px;`;
const SectionTitle = styled.h2`font-size:16px;color:#1f2937;margin:14px 0 4px;`;
const SectionSub = styled.div`font-size:13px;color:#6b7280;margin-bottom:10px;`;
const HoldRow = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:12px;
  border:1px solid #f0e0a8;background:#fffdf3;border-radius:10px;padding:12px 14px;
`;
const HoldTimer = styled.div`
  font-size:12px;margin-top:3px;
  color:${(p) => (p.$urgent ? "#c0483a" : "#8a9996")};
  font-weight:${(p) => (p.$urgent ? 700 : 400)};
`;
const PayBtn = styled.button`
  flex:none;border:none;border-radius:8px;padding:9px 18px;cursor:pointer;
  background:#5d8f24;color:#fff;font-size:14px;font-weight:600;
`;
const List = styled.div`display:flex;flex-direction:column;gap:10px;`;
const Row = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  background:#fff;border:1px solid #eef0f2;border-radius:12px;padding:16px 18px;cursor:pointer;
  transition:box-shadow .2s ease;&:hover{box-shadow:0 6px 16px rgba(0,0,0,.08);}
`;
const TName = styled.div`font-weight:700;color:#1f2937;`;
const TMeta = styled.div`font-size:12.5px;color:#6b7280;margin-top:4px;`;
const Status = styled.div`font-size:12px;font-weight:800;letter-spacing:.4px;`;

export default MyTicketsPage;
